import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Layouts
import MainLayout from '../layouts/MainLayout/MainLayout';
import AuthLayout from '../layouts/AuthLayout/AuthLayout';
import AdminLayout from '../layouts/AdminLayout/AdminLayout';
import RoleLayout from '../layouts/RoleLayout/RoleLayout';

// Auth Pages
import Login from '../pages/Auth/Login';
import Register from '../pages/Auth/Register';
import ForgotPassword from '../pages/Auth/ForgotPassword';
import ResetPassword from '../pages/Auth/ResetPassword';

// Public Pages
import Home from '../pages/Home/Home';
import About from '../pages/About/About';
import Contact from '../pages/Contact/Contact';
import Blog from '../pages/Blog/Blog';
import BlogDetail from '../pages/Blog/BlogDetail';

// Job Pages (Public)
import JobList from '../pages/Jobs/JobList';
import JobDetails from '../pages/Jobs/JobDetails';
import ApplyJob from '../pages/Jobs/ApplyJob';

// Protected Route Component
import ProtectedRoute from '../components/common/ProtectedRoute/ProtectedRoute';

// Super Admin Pages
import SuperAdminDashboard from '../pages/SuperAdmin/Dashboard';
import ManageAdmins from '../pages/SuperAdmin/ManageAdmins';

// Admin Pages
import AdminDashboard from '../pages/Admin/Dashboard';
import ManageUsers from '../pages/Admin/ManageUsers';
import UserVerification from '../pages/Admin/UserVerification';
import ManageJobs from '../pages/Admin/ManageJobs';
import ManageCompanies from '../pages/Admin/ManageCompanies';
import ManageApplications from '../pages/Admin/ManageApplications';
import MasterData from '../pages/Admin/MasterData';
import Reports from '../pages/Admin/Reports';
import Notifications from '../pages/Admin/Notifications';
import Settings from '../pages/Admin/Settings';

// Employer Pages
import EmployerDashboard from '../pages/Employer/Dashboard';
import PostJob from '../pages/Employer/PostJob';
import MyJobs from '../pages/Employer/MyJobs';
import MyJobsList from '../pages/Employer/jobList';
import JobDetail from '../pages/Employer/JobDetail';
import EditJob from '../pages/Employer/EditJob';
import JobApplications from '../pages/Employer/JobApplications';
import ApplicationsJobsList from '../pages/Employer/ApplicationsJobsList';
import CompanyProfile from '../pages/Employer/CompanyProfile';
import ConsultantRequests from '../pages/Employer/ConsultantRequests';
import ConsultantJobApproval from '../pages/Employer/ConsultantJobApproval';
import FindCandidates from '../pages/Employer/FindCandidates';
import EmployerGuard from '../components/EmployerGuard';
import CandidateGuard from '../components/CandidateGuard';
import ConsultantGuard from '../components/ConsultantGuard';

// Candidate Pages
import CandidateDashboard from '../pages/Candidate/Dashboard';
import MyApplications from '../pages/Candidate/MyApplications';
import SavedJobs from '../pages/Candidate/SavedJobs';
import CompleteProfile from '../pages/Candidate/CompleteProfile';

// Consultant Pages
import ConsultantDashboard from '../pages/Consultant/Dashboard';
import CompanyManagement from '../pages/Consultant/CompanyManagement';
import JobManagement from '../pages/Consultant/JobManagement';
import ApplicationManagement from '../pages/Consultant/ApplicationManagement';
import ConsultantProfile from '../pages/Consultant/ConsultantProfile';
import ConsultantCreateJob from '../pages/Consultant/CreateJob';
import ConsultantEditJob from '../pages/Consultant/EditConsultantsJobs';
import ConsultantFindCandidates from '../pages/Consultant/FindCandidates';
import ConsultantCandidates from '../pages/Consultant/ConsultantCandidates';


// Error Pages
import NotFound from '../pages/NotFound/NotFound';
import Unauthorized from '../pages/Unauthorized/Unauthorized';

const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();

  // Redirect authenticated users from auth pages 
  const AuthRedirect = ({ children }) => {
    if (isAuthenticated) {
      const role = user?.role;
      switch (role) {
        case 'super_admin':
        case 'admin':
        case 'subadmin':
          return <Navigate to="/admin/dashboard" replace />;
        case 'employer':
          return <Navigate to="/employer/dashboard" replace />;
        case 'candidate':
          return <Navigate to="/candidate/dashboard" replace />;
        case 'consultant':
          return <Navigate to="/consultant/dashboard" replace />;
        default:
          return <Navigate to="/" replace />;
      }
    }
    return children;
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} /> 
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/contact" element={<Contact />} />
      </Route>

      {/* Job Routes → sidebar layout for logged-in candidates, public layout otherwise */}
      <Route element={<RoleLayout />}>
        <Route path="/jobs" element={<JobList />} />
        <Route path="/jobs/:id" element={<JobDetails />} />

        {/* Apply Job → Candidate + Email Verified */}
        <Route
          path="/jobs/:id/apply"
          element={
            <ProtectedRoute allowedRoles={['candidate']}>
              <CandidateGuard requireEmailVerification={true}>
                <ApplyJob />
              </CandidateGuard>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
        <Route path="/register" element={<AuthRedirect><Register /></AuthRedirect>} />
        <Route path="/forgot-password" element={<AuthRedirect><ForgotPassword /></AuthRedirect>} />
        <Route path="/reset-password" element={<AuthRedirect><ResetPassword /></AuthRedirect>} />
      </Route>

      {/* Super Admin Routes */}
      <Route path="/super-admin/*" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <Routes>
            <Route path="dashboard" element={<SuperAdminDashboard />} />
            <Route path="admins" element={<ManageAdmins />} />
            {/* Add more super admin routes */}
          </Routes>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin', 'subadmin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<ManageUsers />} />
        <Route path="verifications" element={<UserVerification />} />
        <Route path="jobs" element={<ManageJobs />} />
        <Route path="companies" element={<ManageCompanies />} />
        <Route path="applications" element={<ManageApplications />} />
        <Route path="master-data" element={<MasterData />} />
        <Route path="reports" element={<Reports />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Employer Routes */}
      <Route path="/employer" element={
        <ProtectedRoute allowedRoles={['employer']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<EmployerDashboard />} />
        <Route path="find-candidates" element={
          <EmployerGuard requireEmailVerification={true}>
            <FindCandidates />
          </EmployerGuard>
        } />
        <Route path="post-job" element={<PostJob />} />
        <Route path="my-jobs" element={
          <EmployerGuard requireEmailVerification={true}>
            <MyJobs />
          </EmployerGuard>
        } />
        {/* <Route path="jobs/:id" element={
          <EmployerGuard requireEmailVerification={true}>
            <JobDetail />
          </EmployerGuard>
        } /> */}
        <Route path="jobs/:id/edit" element={
          <EmployerGuard requireEmailVerification={true}>
            <EditJob />
          </EmployerGuard>
        } />
        <Route path="applications" element={
          <EmployerGuard requireEmailVerification={true}>
            <ApplicationsJobsList />
          </EmployerGuard>
        } />
        <Route path="jobs/:jobId/applications" element={
          <EmployerGuard requireEmailVerification={true}>
            <JobApplications />
          </EmployerGuard>
        } />
        <Route path="profile" element={<CompanyProfile />} />
        <Route path="consultant-requests" element={<ConsultantRequests />} />
        <Route path="consultant-jobs" element={<ConsultantJobApproval />} />
      </Route>

      {/* Candidate Routes */}
      <Route
        path="/candidate"
        element={
          <ProtectedRoute allowedRoles={['candidate']}>
            <CandidateGuard requireEmailVerification={true}>
              <AdminLayout />
            </CandidateGuard>
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<CandidateDashboard />} />
        <Route path="profile" element={<CompleteProfile />} />
        <Route path="my-applications" element={<MyApplications />} />
        <Route path="applications/:id" element={<MyApplications />} />
        <Route path="saved-jobs" element={<SavedJobs />} />
      </Route>


      {/* Consultant Routes */}
      <Route path="/consultant" element={
        <ProtectedRoute allowedRoles={['consultant']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<ConsultantDashboard />} />
        <Route path="profile" element={
          <ConsultantGuard requireEmailVerification={false}>
            <ConsultantProfile />
          </ConsultantGuard>
        } />
        <Route path="companies" element={
          <ConsultantGuard requireEmailVerification={true}>
            <CompanyManagement />
          </ConsultantGuard>
        } />
        <Route path="jobs" element={
          <ConsultantGuard requireEmailVerification={true}>
            <JobManagement />
          </ConsultantGuard>
        } />
        <Route path="jobs/create" element={
          <ConsultantGuard requireEmailVerification={true}>
            <ConsultantCreateJob />
          </ConsultantGuard>
        } />
        <Route path="jobs/edit/:jobId" element={
          <ConsultantGuard requireEmailVerification={true}>
            <ConsultantEditJob />
          </ConsultantGuard>
        } />
        <Route path="applications" element={
          <ConsultantGuard requireEmailVerification={true}>
            <ApplicationManagement />
          </ConsultantGuard>
        } />
        <Route path="find-candidates" element={
          <ConsultantGuard requireEmailVerification={true}>
            <ConsultantFindCandidates />
          </ConsultantGuard>
        } />
        <Route path="my-candidates" element={
          <ConsultantGuard requireEmailVerification={true}>
            <ConsultantCandidates />
          </ConsultantGuard>
        } />
      </Route>

      {/* Error Routes */}
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
